import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PenLine, BookOpenCheck, ArrowLeft, CheckCircle2, ArrowDown, UserPlus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { PdfViewer } from '../../../components/PdfViewer';
import { salesApi, apiError, contractPdfUrl } from '../../salesApi';
import { useT } from '../../salesI18n';
import { SignaturePad, padPng } from './SignaturePad';
import { RepSignatureField, repSignaturePng } from './RepSignatureField';

// Passo 1: o cliente lê o pacote do contrato inteiro (PDF com zoom); só libera ao chegar no fim.
// Passo 2: comprador 1 (nome + assinatura), 2º comprador opcional e assinatura do vendedor (salva ou na hora) → replicadas em todas as páginas.
export const ContractSignDialog = ({ open, onOpenChange, contract, lead, onSigned }) => {
  const { t } = useT();
  const sigRef = useRef(null);
  const sig2Ref = useRef(null);
  const [step, setStep] = useState('read');
  const [readDone, setReadDone] = useState(false);
  const [name, setName] = useState(lead?.customer_name || '');
  const [second, setSecond] = useState(false);
  const [name2, setName2] = useState('');
  const [repState, setRepState] = useState(null);
  const [busy, setBusy] = useState(false);
  const onRepChange = useCallback((s) => setRepState(s), []);

  useEffect(() => {
    if (!open) return;
    setStep('read'); setReadDone(false); setName(lead?.customer_name || '');
    const sb = contract?.details?.second_buyer_name || '';
    setSecond(Boolean(sb)); setName2(sb);
  }, [open, contract?.id, contract?.details?.second_buyer_name, lead?.customer_name]);

  const submit = async () => {
    if (!name.trim()) { toast.error(`${t.signerName}: ${t.required}`); return; }
    const png = padPng(sigRef);
    if (!png) { toast.error(t.signatureRequired); return; }
    const png2 = second ? padPng(sig2Ref) : null;
    if (second && (!name2.trim() || !png2)) { toast.error(`${t.buyer2}: ${!name2.trim() ? t.required : t.signatureRequired}`); return; }
    const repPng = repSignaturePng(repState);
    if (!repState?.saved && !repPng) { toast.error(t.repSignatureRequired); return; }
    setBusy(true);
    try {
      const body = { signer_name: name.trim(), signature_png_base64: png, second_signer_name: second ? name2.trim() : null, second_signature_png_base64: png2, rep_signature_png_base64: repPng || null };
      const { data } = await salesApi.post(`/contracts/${contract.id}/sign`, body);
      toast.success(t.signed);
      onSigned?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!contract) return null;
  const StepPill = ({ id, n, label }) => (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 h-7 text-[11px] font-bold ${step === id ? 'bg-slate-900 text-white' : readDone && id === 'read' ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-500'}`} data-testid={`contract-step-${id}`}>
      {readDone && id === 'read' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-4 w-4 rounded-full bg-white/20 text-[10px] flex items-center justify-center">{n}</span>} {label}
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[calc(100vw-1rem)] sm:w-full h-[94dvh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="contract-sign-dialog">
        <DialogHeader className="px-4 sm:px-6 pt-4 pb-3 border-b border-slate-200/70 shrink-0">
          <DialogTitle className="font-display flex flex-wrap items-center gap-2">{t.signContract} · {contract.number}
            <span className="flex items-center gap-1.5 ml-auto"><StepPill id="read" n={1} label={t.stepRead} /><StepPill id="sign" n={2} label={t.stepSign} /></span>
          </DialogTitle>
        </DialogHeader>

        {step === 'read' ? (
          <>
            <div className="px-4 sm:px-6 py-2 flex items-center gap-2 text-xs font-semibold text-slate-700 bg-amber-50/70 border-b border-amber-100 shrink-0" data-testid="contract-read-banner">
              <BookOpenCheck className="h-4 w-4 text-amber-700 shrink-0" /> {t.readContractFull}
            </div>
            <div className="flex-1 min-h-0 flex flex-col" data-testid="contract-reader">
              <PdfViewer src={contractPdfUrl(contract.id)} filename={`${t.contract} ${contract.number}.pdf`} labels={t.pdfViewer} onReachEnd={() => setReadDone(true)} showOpenTab={false} className="flex-1 min-h-0" testId="contract-pdf" />
            </div>
            <div className="px-4 sm:px-6 py-3 border-t border-slate-200/70 flex flex-col sm:flex-row sm:items-center gap-2 shrink-0 bg-white">
              <p className={`text-xs font-semibold flex items-center gap-1.5 flex-1 ${readDone ? 'text-teal-700' : 'text-slate-500'}`} data-testid="contract-read-status">
                {readDone ? <><CheckCircle2 className="h-4 w-4" /> {t.readContractDone}</> : <><ArrowDown className="h-4 w-4 animate-bounce" /> {t.readContractHint}</>}
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                <Button variant="outline" className="h-11 w-full sm:w-auto" onClick={() => onOpenChange(false)} data-testid="contract-sign-cancel">{t.cancel}</Button>
                <Button className="h-11 w-full sm:w-auto" disabled={!readDone} onClick={() => setStep('sign')} data-testid="contract-read-continue"><PenLine className="h-4 w-4 mr-1 shrink-0" /> <span className="truncate">{t.readContinue}</span></Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 sales-kanban-scroll" data-testid="contract-sign-step">
            <button type="button" onClick={() => setStep('read')} className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:underline" data-testid="contract-back-to-read"><ArrowLeft className="h-3.5 w-3.5" /> {t.backToContract}</button>

            <div className="space-y-3" data-testid="contract-buyer1">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t.buyer1}</p>
              <div>
                <Label className="text-xs font-semibold text-slate-600">{t.signerName}</Label>
                <Input data-testid="contract-signer-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 mt-1" />
              </div>
              <SignaturePad ref={sigRef} label={t.signBuyerHere} clearLabel={t.clearSignature} testId="contract-signature-pad" />
            </div>

            {second ? (
              <div className="space-y-3 rounded-2xl border border-slate-200/70 p-3" data-testid="contract-buyer2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t.buyer2}</p>
                  <button type="button" onClick={() => setSecond(false)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:underline" data-testid="contract-remove-buyer2"><X className="h-3.5 w-3.5" /> {t.removeSecondBuyer}</button>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">{t.secondSignerName}</Label>
                  <Input data-testid="contract-signer2-name" value={name2} onChange={(e) => setName2(e.target.value)} className="h-11 mt-1" />
                </div>
                <SignaturePad ref={sig2Ref} label={t.signBuyerHere} clearLabel={t.clearSignature} testId="contract-signature2-pad" height={150} />
              </div>
            ) : (
              <button type="button" onClick={() => setSecond(true)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-teal-200 bg-white/70 text-xs font-semibold text-teal-800 hover:bg-teal-50 transition-colors" data-testid="contract-add-buyer2"><UserPlus className="h-4 w-4" /> {t.addSecondBuyer}</button>
            )}

            <RepSignatureField onChange={onRepChange} />

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="h-11" onClick={() => onOpenChange(false)} data-testid="contract-sign-cancel-step2">{t.cancel}</Button>
              <Button className="h-11" onClick={submit} disabled={busy} data-testid="contract-sign-submit">{busy ? t.loading : t.confirmSign}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
